import React, { useEffect, useState, useRef, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import api from '../services/api';

// Ícones
import {
  Car,
  Bike,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  AlertTriangle,
  Wrench,
  Siren,
  ThumbsUp,
  Trash2,
  Navigation,
  Clock,
  User,
  MapPin,
  Menu,
  X,
  Eye,
  EyeOff,
  Search,
  Compass,
  LocateFixed,
  Plus,
  Minus,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Route,
  Layers3,
  Map as MapIcon,
  Satellite,
  Mountain,
  Crosshair,
  ArrowUp,
  ShieldAlert,
  TrafficCone,
  School,
  Locate,
  RotateCcw
} from 'lucide-react';

import 'mapbox-gl/dist/mapbox-gl.css';

// ======================================================
// TOKEN MAPBOX
// ======================================================
const MAPBOX_TOKEN = 'pk.eyJ1IjoiYXBpLW1hcHMtdW5pIiwiYSI6ImNtcGs5anBmbzF0emQyc3ExbmM5eGxjNWoifQ.1-gMjZOnHnAUir5ptXZ1Zg';
mapboxgl.accessToken = MAPBOX_TOKEN;

// ======================================================
// CSP WORKER FIX
// ======================================================
try {
  const workerBlob = new Blob(
    [
      `importScripts('https://api.mapbox.com/mapbox-gl-js/v${mapboxgl.version}/mapbox-gl-csp-worker.js');`
    ],
    { type: 'application/javascript' }
  );
  mapboxgl.workerUrl = URL.createObjectURL(workerBlob);
} catch (e) {
  console.error('Erro CSP Worker:', e);
}

// ======================================================
// HELPERS
// ======================================================
const formatarDataHora = (isoString) => {
  if (!isoString) return 'Agora mesmo';
  try {
    const data = new Date(isoString);
    if (isNaN(data.getTime())) return 'Recentemente';
    return data.toLocaleString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit'
    });
  } catch (e) {
    return 'Recentemente';
  }
};

const formatarDistancia = (metros) => {
  if (!metros) return '0 m';
  if (metros < 1000) return `${Math.round(metros)} m`;
  return `${(metros / 1000).toFixed(1)} km`;
};

const calcularBearing = (start, end) => {
  if (!start || !end) return 0;
  const startLat = (start[1] * Math.PI) / 180;
  const startLng = (start[0] * Math.PI) / 180;
  const endLat = (end[1] * Math.PI) / 180;
  const endLng = (end[0] * Math.PI) / 180;
  const dLng = endLng - startLng;
  const y = Math.sin(dLng) * Math.cos(endLat);
  const x = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
};

const suavizarMovimento = (origem, destino, fator = 0.18) => {
  if (!origem) return destino;
  return [
    origem[0] + (destino[0] - origem[0]) * fator,
    origem[1] + (destino[1] - origem[1]) * fator
  ];
};

// ======================================================
// TTS GPS
// ======================================================
const falarInstrucao = (texto, vozAtiva = true) => {
  if (!vozAtiva) return;
  if (!window.speechSynthesis || !texto) return;
  try {
    window.speechSynthesis.cancel();
    const speech = new SpeechSynthesisUtterance(texto);
    speech.lang = 'pt-BR';
    speech.rate = 1.08;
    speech.pitch = 1;
    speech.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const vozPt = voices.find((v) => v.lang.toLowerCase().includes('pt'));
    if (vozPt) speech.voice = vozPt;
    window.speechSynthesis.speak(speech);
  } catch (e) {
    console.error(e);
  }
};

// ======================================================
// COMPONENTE PRINCIPAL
// ======================================================
const MapaEscola = ({ lat, lng, nome, setMetricasRota, tema }) => {
  
  // REFS DO MAPBOX E ANIMAÇÃO
  const containerRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerVeiculoRef = useRef(null);
  const markerDestinoRef = useRef(null);
  const markerPreviewRef = useRef(null);
  const markersAlertasRef = useRef([]);
  const animacaoFrameRef = useRef(null);
  const ultimaPosicaoRef = useRef(null);
  const ultimaAtualizacaoRef = useRef(0);
  const ultimaInstrucaoRef = useRef('');
  const rotaGeojsonRef = useRef(null);

  // ESTADOS DO COMPONENTE
  const [minhaPosicao, setMinhaPosicao] = useState(null);
  const [heading, setHeading] = useState(0);
  const [veiculo, setVeiculo] = useState('car');
  const [vozAtiva, setVozAtiva] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [modoNavegacao, setModoNavegacao] = useState(false);
  const [focoAtivo, setFocoAtivo] = useState(true);
  const [menuAberto, setMenuAberto] = useState(false);
  const [mostrarEventos, setMostrarEventos] = useState(true);
  const [mostrarToastDestino, setMostrarToastDestino] = useState(false);
  const [mostrarPainelCamadas, setMostrarPainelCamadas] = useState(false);
  const [mapStyleMode, setMapStyleMode] = useState('streets');
  const [mostrarPredios3D, setMostrarPredios3D] = useState(true);
  const [mostrarRelevo, setMostrarRelevo] = useState(true);
  const [mostrarRadarVelocidade, setMostrarRadarVelocidade] = useState(true);
  const [velocidadeAtual, setVelocidadeAtual] = useState(0);
  const [alertas, setAlertas] = useState([]);
  const [pontoClique, setPontoClique] = useState(null);
  const [nomeRuaAtual, setNomeRuaAtual] = useState('');
  const [alertaSelecionado, setAlertaSelecionado] = useState(null);
  const [destinoSelecionado, setDestinoSelecionado] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [instrucoesRota, setInstrucoesRota] = useState([]);
  const [instrucaoAtual, setInstrucaoAtual] = useState(null);
  const [partidaTexto, setPartidaTexto] = useState('Minha localização');
  const [destinoTexto, setDestinoTexto] = useState(nome || '');
  const [coordenadasPartida, setCoordenadasPartida] = useState(null);
  const [metricasLocais, setMetricasLocais] = useState({ distancia: '0.0', tempo: 0 });

  const [coordenadasDestino, setCoordenadasDestino] = useState(() => {
    const defaultLng = parseFloat(lng) || -46.633308;
    const defaultLat = parseFloat(lat) || -23.55052;
    return [defaultLng, defaultLat];
  });

  // ======================================================
  // SINCRONIZAÇÃO DE REFERÊNCIAS PARA EVITAR TRAVAMENTOS (ANTI-STALE)
  // ======================================================
  const modoNavegacaoRef = useRef(modoNavegacao);
  const focoAtivoRef = useRef(focoAtivo);
  const headingRef = useRef(heading);
  const instrucoesRotaRef = useRef(instrucoesRota);
  const vozAtivaRef = useRef(vozAtiva);
  const coordenadasPartidaRef = useRef(coordenadasPartida);

  useEffect(() => { modoNavegacaoRef.current = modoNavegacao; }, [modoNavegacao]);
  useEffect(() => { focoAtivoRef.current = focoAtivo; }, [focoAtivo]);
  useEffect(() => { headingRef.current = heading; }, [heading]);
  useEffect(() => { instrucoesRotaRef.current = instrucoesRota; }, [instrucoesRota]);
  useEffect(() => { vozAtivaRef.current = vozAtiva; }, [vozAtiva]);
  useEffect(() => { coordenadasPartidaRef.current = coordenadasPartida; }, [coordenadasPartida]);

  // CONFIGURAÇÃO DOS ESTILOS DO MAPA
  const stylesMap = useMemo(() => {
    return {
      streets: tema === 'dark' ? 'mapbox://styles/mapbox/navigation-night-v1' : 'mapbox://styles/mapbox/navigation-day-v1',
      satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
      dark: 'mapbox://styles/mapbox/dark-v11',
      outdoors: 'mapbox://styles/mapbox/outdoors-v12'
    };
  }, [tema]);

  const mapStyleUrl = stylesMap[mapStyleMode] || stylesMap.streets;

  // INICIALIZAÇÃO DO MAPA
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyleUrl,
      center: coordenadasDestino,
      zoom: 16.5,
      pitch: 60,
      bearing: -17,
      antialias: true
    });

    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'bottom-right');

    const desativarFocoAutomatico = () => {
      setFocoAtivo(false);
    };

    // Ouvintes padrão de detecção de gestos finalizados do Mapbox
    map.on('dragstart', desativarFocoAutomatico);
    map.on('zoomstart', desativarFocoAutomatico);
    map.on('pitchstart', desativarFocoAutomatico);
    map.on('rotatestart', desativarFocoAutomatico);

    // SOLUÇÃO DO BLOQUEIO: Desativa na hora exata em que o dedo encosta ou o mouse clica
    map.on('mousedown', desativarFocoAutomatico);
    map.on('touchstart', desativarFocoAutomatico);

    map.on('click', async (e) => {
      const clickLng = e.lngLat.lng;
      const clickLat = e.lngLat.lat;
      setAlertaSelecionado(null);
      setDestinoSelecionado(null);
      setPontoClique({ lat: clickLat, lng: clickLng });
      setNomeRuaAtual('Buscando endereço...');

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${clickLat}&lon=${clickLng}&zoom=18`,
          { headers: { 'Accept-Language': 'pt-BR' } }
        );
        const data = await response.json();
        if (data?.address) {
          const rua = data.address.road || data.address.suburb || data.address.pedestrian || 'Via sem nome';
          setNomeRuaAtual(rua);
        } else {
          setNomeRuaAtual('Via não identificada');
        }
      } catch (error) {
        setNomeRuaAtual('Próximo à via atual');
      }
    });

    map.on('style.load', () => {
      aplicarRecursos3D();
      recalculaRotaOSRM();
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [mapStyleUrl]);

  // APLICA RECURSOS 3D E AMBIENTE
  const aplicarRecursos3D = () => {
    const map = mapRef.current;
    if (!map) return;

    if (mostrarRelevo) {
      if (!map.getSource('mapbox-dem')) {
        map.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem',
          tileSize: 512,
          maxzoom: 14
        });
      }
      map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.15 });
    } else {
      map.setTerrain(null);
    }

    const layers = map.getStyle().layers;
    const labelLayerId = layers.find((layer) => layer.type === 'symbol' && layer.layout['text-field'])?.id;

    if (mostrarPredios3D && !map.getLayer('3d-buildings')) {
      map.addLayer(
        {
          id: '3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 15,
          paint: {
            'fill-extrusion-color': tema === 'dark' ? '#2b3445' : '#cbd5e1',
            'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'height']],
            'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'min_height']],
            'fill-extrusion-opacity': 0.72
          }
        },
        labelLayerId
      );
    }

    map.setFog({
      range: [0.5, 10],
      color: tema === 'dark' ? '#09090b' : '#ffffff',
      'horizon-blend': 0.12,
      'space-color': tema === 'dark' ? '#050816' : '#cce5ff',
      'star-intensity': 0.2
    });
  };

  // PREVIEW DO MARCADOR DE CLIQUE
  useEffect(() => {
    if (!mapRef.current) return;
    if (!pontoClique) {
      if (markerPreviewRef.current) {
        markerPreviewRef.current.remove();
        markerPreviewRef.current = null;
      }
      return;
    }

    if (markerPreviewRef.current) {
      markerPreviewRef.current.setLngLat([pontoClique.lng, pontoClique.lat]);
    } else {
      const el = document.createElement('div');
      el.className = 'animate-bounce';
      el.innerHTML = `
        <div style="width:34px; height:34px; background:#64748b; border:3px solid #fff; border-radius:999px; display:flex; align-items:center; justify-content:center; box-shadow:0 6px 18px rgba(0,0,0,0.45); color:white;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="9"/></svg>
        </div>
        <div style="width:0; height:0; border-left:6px solid transparent; border-right:6px solid transparent; border-top:8px solid white; margin:-2px auto 0 auto;"></div>
      `;
      markerPreviewRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([pontoClique.lng, pontoClique.lat])
        .addTo(mapRef.current);
    }
  }, [pontoClique]);

  // CICLO GEOLOCATION EM TEMPO REAL AJUSTADO (SEM DEPENDÊNCIAS ESTÁTICAS MUTÁVEIS)
  useEffect(() => {
    if (!navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!position?.coords) return;
        const agora = Date.now();

        if (agora - ultimaAtualizacaoRef.current < 750) return;
        ultimaAtualizacaoRef.current = agora;

        const { latitude, longitude, heading: gpsHeading, speed } = position.coords;
        const velocidadeKmh = speed ? speed * 3.6 : 0;
        setVelocidadeAtual(Math.round(velocidadeKmh));

        const novaPosicaoReal = [longitude, latitude];
        let posicaoFinal = novaPosicaoReal;

        if (ultimaPosicaoRef.current) {
          posicaoFinal = suavizarMovimento(
            ultimaPosicaoRef.current,
            novaPosicaoReal,
            modoNavegacaoRef.current ? 0.26 : 0.16
          );
        }

        ultimaPosicaoRef.current = posicaoFinal;
        setMinhaPosicao(posicaoFinal);

        if (modoNavegacaoRef.current) {
          setCoordenadasPartida(posicaoFinal);
        } else if (!coordenadasPartidaRef.current) {
          setCoordenadasPartida(posicaoFinal);
        }

        let headingFinal = headingRef.current;
        if (typeof gpsHeading === 'number' && !isNaN(gpsHeading)) {
          headingFinal = gpsHeading;
        } else if (ultimaPosicaoRef.current) {
          headingFinal = calcularBearing(ultimaPosicaoRef.current, novaPosicaoReal);
        }
        setHeading(headingFinal);

        // Atualiza a posição visual do marcador do carro/moto sem travar a renderização
        if (markerVeiculoRef.current) {
          markerVeiculoRef.current.setLngLat(posicaoFinal);
          const el = markerVeiculoRef.current.getElement();
          if (el) {
            el.style.transition = 'transform 0.2s linear';
            el.style.transform = `translate(-50%, -50%) rotate(${headingFinal}deg)`;
          }
        }

        // AUTO FOLLOW DINÂMICO INTERROMPIDO TOTALMENTE SE O FOCO ATIVO FOR FALSO
        if (focoAtivoRef.current && mapRef.current) {
          cancelAnimationFrame(animacaoFrameRef.current);
          animacaoFrameRef.current = requestAnimationFrame(() => {
            if (!mapRef.current) return;
            if (modoNavegacaoRef.current) {
              mapRef.current.jumpTo({
                center: posicaoFinal,
                zoom: 17.8,
                pitch: 65,
                bearing: headingFinal || 0
              });
            } else {
              mapRef.current.easeTo({
                center: posicaoFinal,
                duration: 800,
                easing: (t) => t * (2 - t)
              });
            }
          });
        }

        // Verifica o avanço do passo de instrução atual
        if (instrucoesRotaRef.current.length && modoNavegacaoRef.current) {
          const proxima = instrucoesRotaRef.current.find((item) => !item.executada);
          if (proxima) {
            const distancia = calcularDistanciaEntrePontos(posicaoFinal, proxima.location);
            if (distancia <= 80) {
              proxima.executada = true;
              setInstrucaoAtual(proxima);
              if (ultimaInstrucaoRef.current !== proxima.voice) {
                ultimaInstrucaoRef.current = proxima.voice;
                falarInstrucao(proxima.voice, vozAtivaRef.current);
              }
            }
          }
        }
      },
      (err) => { console.error('ERRO GPS:', err); },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
      if (animacaoFrameRef.current) {
        cancelAnimationFrame(animacaoFrameRef.current);
      }
    };
  }, []); // Deixamos vazio! O uso inteligente das referências mutáveis dispensa remontagem do GPS.

  const verificarInstrucaoAtual = (posicaoAtual) => {
    if (!instrucoesRota.length || !modoNavegacao) return;
    const proxima = instrucoesRota.find((item) => !item.executada);
    if (!proxima) return;

    const distancia = calcularDistanciaEntrePontos(posicaoAtual, proxima.location);
    if (distancia <= 80) {
      proxima.executada = true;
      setInstrucaoAtual(proxima);
      if (ultimaInstrucaoRef.current !== proxima.voice) {
        ultimaInstrucaoRef.current = proxima.voice;
        falarInstrucao(proxima.voice, vozAtiva);
      }
    }
  };

  const calcularDistanciaEntrePontos = (p1, p2) => {
    if (!p1 || !p2) return 999999;
    const R = 6371e3;
    const φ1 = (p1[1] * Math.PI) / 180;
    const φ2 = (p2[1] * Math.PI) / 180;
    const Δφ = ((p2[1] - p1[1]) * Math.PI) / 180;
    const Δλ = ((p2[0] - p1[0]) * Math.PI) / 180;
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // SINCRONIZAÇÃO DO MARCADOR DO VEÍCULO
  useEffect(() => {
    if (!mapRef.current || !coordenadasPartida) return;

    if (markerVeiculoRef.current) {
      markerVeiculoRef.current.setLngLat(coordenadasPartida);
    } else {
      const el = document.createElement('div');
      el.className = 'waze-driver-avatar pointer-events-none';
      el.innerHTML = `
        <div style="width:48px; height:48px; background:#007aff; border:3px solid #ffffff; border-radius:999px; display:flex; align-items:center; justify-content:center; box-shadow:0 5px 18px rgba(0,122,255,0.55); position:relative;">
          <div style="position:absolute; inset:-5px; border-radius:999px; border:2px solid rgba(0,122,255,0.25); animation:pulseDriver 2s infinite;"></div>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.3">
            <path d="M18.5 13H5.5a.5.5 0 0 1-.5-.5V11c0-1.66 1.34-3 3-3h8c1.66 0 3 1.34 3 3v1.5a.5.5 0 0 1-.5.5zM19 13.5v3.5a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-.5H9v.5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-3.5a.5.5 0 0 1 .5-.5h12a.5.5 0 0 1 .5.5z" fill="#ffffff"/>
          </svg>
        </div>
      `;
      markerVeiculoRef.current = new mapboxgl.Marker(el).setLngLat(coordenadasPartida).addTo(mapRef.current);
    }

    const innerEl = markerVeiculoRef.current.getElement();
    if (innerEl) {
      innerEl.style.transform = `translate(-50%, -50%) rotate(${heading}deg)`;
    }
  }, [coordenadasPartida, heading]);

  useEffect(() => {
    if (lat && lng) {
      setCoordenadasDestino([parseFloat(lng), parseFloat(lat)]);
    }
  }, [lat, lng]);

  // CRIAÇÃO E CONFIGURAÇÃO DO MARCADOR DE DESTINO
  useEffect(() => {
    if (!mapRef.current || !coordenadasDestino) return;

    if (markerDestinoRef.current) {
      markerDestinoRef.current.setLngLat(coordenadasDestino);
    } else {
      const el = document.createElement('div');
      el.className = 'cursor-pointer';
      el.style.display = 'flex';
      el.style.flexDirection = 'column';
      el.style.alignItems = 'center';
      el.innerHTML = `
        <div style="width:46px; height:46px; background:#f43f5e; border:3px solid #ffffff; border-radius:999px; display:flex; align-items:center; justify-content:center; box-shadow:0 5px 18px rgba(244,63,94,0.45); color:white;">
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"/></svg>
        </div>
        <div style="width:0; height:0; border-left:7px solid transparent; border-right:7px solid transparent; border-top:9px solid #fff; margin-top:-2px;"></div>
      `;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        handleDestinoClick();
      });

      markerDestinoRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(coordenadasDestino)
        .addTo(mapRef.current);
    }
  }, [coordenadasDestino]);

  // CÁLCULO DE TRAJETÓRIA VIA OSRM ENGINE
  const recalculaRotaOSRM = async () => {
    if (!coordenadasPartida || !coordenadasDestino || !mapRef.current) return;

    try {
      const profile = veiculo === 'bike' ? 'cycling' : 'driving';
      const url = `https://router.project-osrm.org/route/v1/${profile}/${coordenadasPartida[0]},${coordenadasPartida[1]};${coordenadasDestino[0]},${coordenadasDestino[1]}?overview=full&steps=true&annotations=true&geometries=geojson`.replace(/\s+/g, '');

      const response = await fetch(url);
      const data = await response.json();

      if (data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        const dadosMetricas = {
          distancia: (route.distance / 1000).toFixed(1),
          tempo: Math.round(route.duration / 60)
        };

        setMetricasLocais(dadosMetricas);
        if (setMetricasRota) setMetricasRota(dadosMetricas);
        setMostrarToastDestino(true);

        const geojson = { type: 'Feature', properties: {}, geometry: route.geometry };
        rotaGeojsonRef.current = geojson;

        const map = mapRef.current;
        if (map.getSource('routeSource')) {
          map.getSource('routeSource').setData(geojson);
        } else {
          map.addSource('routeSource', { type: 'geojson', data: geojson });
          map.addLayer({
            id: 'routeLayerShadow',
            type: 'line',
            source: 'routeSource',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#001b44', 'line-width': 12, 'line-opacity': 0.25 }
          });
          map.addLayer({
            id: 'routeLayer',
            type: 'line',
            source: 'routeSource',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#007aff', 'line-width': 7, 'line-opacity': 0.95 }
          });
          map.addLayer({
            id: 'routeGlow',
            type: 'line',
            source: 'routeSource',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#8cc8ff', 'line-width': 2, 'line-opacity': 0.9}
          });
        }

        const steps = route.legs?.[0]?.steps || [];
        const instrucoes = steps.map((step) => {
          const modifier = step.maneuver.modifier || '';
          const type = step.maneuver.type || '';
          let texto = 'Continue';

          if (type === 'depart') texto = 'Siga em frente';
          if (modifier.includes('left')) texto = 'Vire à esquerda';
          if (modifier.includes('right')) texto = 'Vire à direita';
          if (modifier.includes('straight')) texto = 'Continue reto';
          if (type === 'roundabout') texto = 'Entre na rotatória';
          if (type === 'arrive') texto = 'Você chegou ao destino';

          return { ...step, executada: false, voice: texto, text: texto, location: step.maneuver.location };
        });

        setInstrucoesRota(instrucoes);

        if (!modoNavegacao && focoAtivo) {
          const bounds = new mapboxgl.LngLatBounds().extend(coordenadasPartida).extend(coordenadasDestino);
          map.fitBounds(bounds, { padding: { top: 80, bottom: 80, left: 50, right: 50 }, maxZoom: 15, duration: 1200 });
        }
      }
    } catch (e) {
      console.error('Falha ao calcular rota:', e);
    }
  };

  useEffect(() => {
    recalculaRotaOSRM();
  }, [coordenadasPartida, coordenadasDestino, veiculo]);

  // ADICIONA E GERENCIA CAMADAS DE ALERTAS DO BANCO DE DADOS
  useEffect(() => {
    if (!mapRef.current) return;
    markersAlertasRef.current.forEach((m) => m.remove());
    markersAlertasRef.current = [];

    if (!mostrarEventos) return;

    alertas.forEach((alerta) => {
      if (!alerta || !alerta.lat || !alerta.lng) return;

      const coresAlertas = { transito: '#3399ff', policia: '#2c52ed', acidente: '#ff3b30', obras: '#ff9500', buraco: '#78350f' };
      const iconesAlertas = { transito: '🚗', policia: '🚓', acidente: '⚠️', obras: '🚧', buraco: '🕳️' };

      const el = document.createElement('div');
      el.className = 'cursor-pointer transition-transform hover:scale-110';
      el.style.display = 'flex';
      el.style.flexDirection = 'column';
      el.style.alignItems = 'center';
      el.innerHTML = `
        <div style="width:40px; height:40px; background:${coresAlertas[alerta.tipo] || '#6b7280'}; border:3px solid white; border-radius:999px; display:flex; align-items:center; justify-content:center; box-shadow:0 5px 14px rgba(0,0,0,0.35); font-size:18px;">
          ${iconesAlertas[alerta.tipo] || '⚠️'}
        </div>
        <div style="width:0; height:0; border-left:5px solid transparent; border-right:5px solid transparent; border-top:7px solid #ffffff; margin-top:-1px;"></div>
      `;

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        handleAlertaClick(alerta);
      });

      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([parseFloat(alerta.lng), parseFloat(alerta.lat)])
        .addTo(mapRef.current);

      markersAlertasRef.current.push(marker);
    });
  }, [alertas, mostrarEventos]);

  const carregarAlertasDoBanco = async () => {
    try {
      const res = await api.get('/alertas-transito');
      setAlertas(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    carregarAlertasDoBanco();
    const interval = setInterval(carregarAlertasDoBanco, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleAlertaClick = async (alerta) => {
    setPontoClique(null);
    setDestinoSelecionado(null);
    let alertaAtualizado = { ...alerta };

    if (!alerta.logradouro || alerta.logradouro === 'Via informada') {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${alerta.lat}&lon=${alerta.lng}&zoom=18`,
          { headers: { 'Accept-Language': 'pt-BR' } }
        );
        const data = await response.json();
        if (data?.address) {
          alertaAtualizado.logradouro = data.address.road || data.address.suburb || data.address.pedestrian || 'Via informada';
        }
      } catch (error) {}
    }

    const proximos = alertas.filter((a) => a.tipo === alerta.tipo || Math.abs(a.lat - alerta.lat) < 0.002);
    setAlertaSelecionado(alertaAtualizado);
    const idx = proximos.findIndex((p) => p.id === alerta.id);
    setSlideIndex(idx >= 0 ? idx : 0);
  };

  const handleDestinoClick = async () => {
    setPontoClique(null);
    setAlertaSelecionado(null);
    setDestinoSelecionado({
      nome: destinoTexto || nome || 'Destino Definido',
      lng: coordenadasDestino[0],
      lat: coordenadasDestino[1],
      logradouro: 'Carregando endereço...'
    });

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coordenadasDestino[1]}&lon=${coordenadasDestino[0]}&zoom=18`,
        { headers: { 'Accept-Language': 'pt-BR' } }
      );
      const data = await response.json();
      if (data?.address) {
        const rua = data.address.road || data.address.suburb || data.display_name.split(',')[0];
        const city = data.address.city || data.address.town || '';
        setDestinoSelecionado((prev) => ({ ...prev, logradouro: `${rua} - ${city}` }));
      }
    } catch (err) {
      setDestinoSelecionado((prev) => ({ ...prev, logradouro: 'Endereço indisponível' }));
    }
  };

  const buscarEnderecoGeocode = async (texto, campo) => {
    if (!texto) return;
    if (texto === 'Minha localização' || texto === 'Minha localização (GPS)') {
      if (minhaPosicao) {
        setCoordenadasPartida(minhaPosicao);
        if (mapRef.current) mapRef.current.flyTo({ center: minhaPosicao });
      }
      return;
    }

    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(texto)}&limit=1`);
      const data = await response.json();
      if (data && data.length > 0) {
        const nLng = parseFloat(data[0].lon);
        const nLat = parseFloat(data[0].lat);

        if (campo === 'partida') {
          setCoordenadasPartida([nLng, nLat]);
        } else {
          setCoordenadasDestino([nLng, nLat]);
        }

        if (mapRef.current) {
          mapRef.current.flyTo({ center: [nLng, nLat], zoom: 15 });
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const calcularNovaRotaCompleta = async () => {
    await buscarEnderecoGeocode(partidaTexto, 'partida');
    await buscarEnderecoGeocode(destinoTexto, 'destino');
    setModoNavegacao(false);
    setFocoAtivo(true);
    setMenuAberto(false);
  };

  const dispararNavegacaoWaze = () => {
    falarInstrucao(`Iniciando navegação para ${destinoTexto || nome}`, vozAtiva);
    setModoNavegacao(true);
    setFocoAtivo(true);
    setDestinoSelecionado(null);

    if (mapRef.current) {
      mapRef.current.flyTo({
        center: coordenadasPartida || minhaPosicao || coordenadasDestino,
        zoom: 17.7,
        pitch: 65,
        bearing: heading || 0,
        duration: 900
      });
    }
  };

  const aplicarGpsComoPartida = () => {
    if (minhaPosicao) {
      setCoordenadasPartida(minhaPosicao);
      setPartidaTexto('Minha localização (GPS)');
      setFocoAtivo(true);
      if (mapRef.current) mapRef.current.flyTo({ center: minhaPosicao });
    }
  };

  const adicionarAlertaNoPonto = async (tipoAlerta) => {
    if (!pontoClique) return;
    const minutes = tipoAlerta === 'buraco' || tipoAlerta === 'obras' ? 240 : 30;
    let nomeCriador = 'Motorista';

    try {
      const stored = localStorage.getItem('usuario');
      if (stored) {
        const usuarioLogado = JSON.parse(stored);
        if (usuarioLogado?.nome) nomeCriador = usuarioLogado.nome;
      }
    } catch (e) {}

    try {
      await api.post('/alertas-transito', {
        lat: pontoClique.lat,
        lng: pontoClique.lng,
        tipo: tipoAlerta,
        duracao_minutos: minutes,
        criado_por: nomeCriador,
        logradouro: nomeRuaAtual !== 'Buscando endereço...' ? nomeRuaAtual : 'Via informada',
        data_criacao: new Date().toISOString()
      });

      setPontoClique(null);
      carregarAlertasDoBanco();
      falarInstrucao('Alerta enviado com sucesso', vozAtiva);
    } catch (err) {
      console.error(err);
    }
  };

  const confirmarAlerta = async (id) => {
    try {
      await api.put(`/alertas-transito/${id}/confirmar`);
      carregarAlertasDoBanco();
      if (alertaSelecionado) {
        setAlertaSelecionado((prev) => ({ ...prev, confirmacoes: (prev.confirmacoes || 0) + 1 }));
      }
    } catch (err) {}
  };

  const removerAlertaFalso = async (id) => {
    try {
      await api.put(`/alertas-transito/${id}/remover`);
      carregarAlertasDoBanco();
      setAlertaSelecionado(null);
    } catch (err) {}
  };

  const handleManualZoom = (type) => {
    setFocoAtivo(false);
    if (!mapRef.current) return;
    if (type === 'in') {
      mapRef.current.zoomIn();
    } else {
      mapRef.current.zoomOut();
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch(() => {});
    } else {
      document.exitFullscreen()
        .then(() => setIsFullscreen(false))
        .catch(() => {});
    }
  };

  const nextSlide = () => {
    const proximos = alertas.filter((a) => a.tipo === alertaSelecionado.tipo || Math.abs(a.lat - alertaSelecionado.lat) < 0.002);
    const nextIdx = (slideIndex + 1) % proximos.length;
    setSlideIndex(nextIdx);
    setAlertaSelecionado(proximos[nextIdx]);
  };

  const prevSlide = () => {
    const proximos = alertas.filter((a) => a.tipo === alertaSelecionado.tipo || Math.abs(a.lat - alertaSelecionado.lat) < 0.002);
    const prevIdx = (slideIndex - 1 + proximos.length) % proximos.length;
    setSlideIndex(prevIdx);
    setAlertaSelecionado(proximos[prevIdx]);
  };

  const listSlidesVisiveis = alertaSelecionado ? alertas.filter((a) => a.tipo === alertaSelecionado.tipo || Math.abs(a.lat - alertaSelecionado.lat) < 0.002) : [];

  // FUNÇÃO RECENTRALIZAR MAPA AJUSTADA (RESTAURA O FOCO DE COMPORTAMENTO)
  const recentralizarMapa = () => {
    setFocoAtivo(true);
    const posAlvo = minhaPosicao || coordenadasPartida;

    if (mapRef.current && posAlvo) {
      mapRef.current.easeTo({
        center: posAlvo,
        zoom: modoNavegacao ? 17.5 : 15,
        pitch: modoNavegacao ? 60 : 0,
        bearing: modoNavegacao ? heading : 0,
        duration: 1000
      });
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden font-sans transition-all duration-300 bg-slate-100 dark:bg-zinc-950 isolate-fixed-container ${
        isFullscreen ? 'fixed inset-0 z-[99999] h-screen w-screen rounded-none' : 'h-[720px] rounded-[36px] border border-slate-200 dark:border-zinc-800 shadow-2xl'
      }`}
    >
      {/* TOAST METRICAS DESTINO */}
      {mostrarToastDestino && metricasLocais.distancia !== '0.0' && (
        <div className="absolute top-3 left-2 -translate-x-1/2 z-[5020] w-[94%] max-w-sm bg-blue-600 dark:bg-blue-700 text-white rounded-2xl p-3 shadow-xl flex items-center justify-between border border-blue-500 pointer-events-auto animate-fade-in">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
              <Navigation size={16} className="text-white fill-white" />
            </div>
            <div className="min-w-0">
              <h4 className="text-xs font-black truncate max-w-[180px] text-white">{destinoTexto || nome || 'Destino'}</h4>
              <p className="text-[11px] text-blue-100 font-bold mt-0.5">{metricasLocais.tempo} min • {metricasLocais.distancia} km</p>
            </div>
          </div>
          <button onClick={() => setMostrarToastDestino(false)} className="text-white/80 hover:text-white bg-black/10 p-1 rounded-lg shrink-0 ml-1">
            <X size={13} />
          </button>
        </div>
      )}

      {/* PAINEL DE CONTROLES FLUTUANTES (HUD) */}
      <div className="absolute inset-0 z-[4010] pointer-events-none flex flex-col justify-between p-3.5">
        <div className="flex justify-between items-start">
          <div className="flex flex-col items-start gap-2.5 pointer-events-auto">
            <button onClick={() => setMenuAberto(true)} className="w-11 h-11 bg-white/95 dark:bg-zinc-900/95 text-slate-700 dark:text-zinc-200 rounded-2xl flex items-center justify-center shadow-xl border border-slate-100 dark:border-zinc-800 shrink-0 backdrop-blur-md">
              <Menu size={20} />
            </button>

            {metricasLocais.distancia !== '0.0' && (
              <div className="flex flex-col items-center justify-center bg-white/95 dark:bg-zinc-900/95 border border-slate-200 dark:border-zinc-800 p-3 rounded-2xl shadow-xl animate-fade-in backdrop-blur-md">
                <div className="flex items-baseline gap-1 leading-none">
                  <span className="text-lg font-black tracking-tight text-slate-900 dark:text-white">{metricasLocais.tempo}</span>
                  <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase">min</span>
                </div>
                <div className="w-full h-[1px] bg-slate-100 dark:bg-zinc-800 my-2" />
                <span className="text-[11px] font-bold text-slate-600 dark:text-zinc-300 tracking-tight">{metricasLocais.distancia} km</span>
                {modoNavegacao && (
                  <button
                    onClick={() => {
                      setModoNavegacao(false);
                      if (mapRef.current) {
                        mapRef.current.easeTo({ pitch: 0, bearing: 0 });
                      }
                      setFocoAtivo(true);
                    }}
                    className="text-[9px] font-black uppercase text-red-500 hover:underline mt-2 block"
                  >
                    Encerrar
                  </button>
                )}
              </div>
            )}

            {mostrarRadarVelocidade && (
              <div className="bg-white/95 dark:bg-zinc-900/95 rounded-2xl px-4 py-2 border border-slate-200 dark:border-zinc-800 shadow-xl backdrop-blur-md animate-fade-in">
                <div className="flex items-center gap-2">
                  <GaugeIcon />
                  <div>
                    <div className="text-[9px] uppercase font-black text-slate-400">Velocidade</div>
                    <div className="text-lg leading-none font-black text-slate-900 dark:text-white">
                      {velocidadeAtual}
                      <span className="text-[10px] ml-1 text-slate-400">km/h</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-2.5 pointer-events-auto">
            <button onClick={() => setMostrarPainelCamadas((v) => !v)} className="w-10 h-10 rounded-2xl bg-white/95 dark:bg-zinc-900/95 border border-slate-200 dark:border-zinc-800 shadow-xl flex items-center justify-center">
              <Layers3 size={17} className="text-slate-700 dark:text-zinc-200" />
            </button>

            <div className="flex flex-col bg-white/95 dark:bg-zinc-900/95 p-1 rounded-2xl border border-slate-200 dark:border-zinc-800 shadow-xl">
              <button onClick={() => setVeiculo('car')} className={`p-2 rounded-xl transition-all ${veiculo === 'car' ? 'bg-blue-600 text-white' : 'text-slate-500'}`}>
                <Car size={15} />
              </button>
              <button onClick={() => setVeiculo('bike')} className={`p-2 rounded-xl transition-all ${veiculo === 'bike' ? 'bg-blue-600 text-white' : 'text-slate-500'}`}>
                <Bike size={15} />
              </button>
            </div>

            <button onClick={() => setVozAtiva(!vozAtiva)} className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-xl border ${vozAtiva ? 'bg-blue-600 text-white border-blue-500' : 'bg-white/95 dark:bg-zinc-900/95 text-slate-500 border-slate-200 dark:border-zinc-800'}`}>
              {vozAtiva ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>

            <button onClick={toggleFullscreen} className="bg-white/95 dark:bg-zinc-900/95 w-10 h-10 rounded-2xl flex items-center justify-center shadow-xl text-slate-600 dark:text-zinc-200 border border-slate-200 dark:border-zinc-800">
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>

            <div className="flex flex-col gap-1.5 mt-1">
              <button onClick={() => handleManualZoom('in')} className="w-10 h-10 bg-white/95 dark:bg-zinc-900/95 text-slate-800 dark:text-zinc-200 rounded-2xl flex items-center justify-center shadow-xl border border-slate-200 dark:border-zinc-800">
                <Plus size={16} />
              </button>
              <button onClick={() => handleManualZoom('out')} className="w-10 h-10 bg-white/95 dark:bg-zinc-900/95 text-slate-800 dark:text-zinc-200 rounded-2xl flex items-center justify-center shadow-xl border border-slate-200 dark:border-zinc-800">
                <Minus size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* CONTAINER INFERIOR (HUD BOTÕES) */}
        <div className="w-full flex flex-col items-end gap-2 mt-auto">
          {modoNavegacao && {instrucaoAtual} && (
            <div className="w-full max-w-md mx-auto bg-blue-600 text-white rounded-3xl p-4 shadow-2xl pointer-events-auto animate-slide-up border border-blue-500">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
                  <Navigation size={22} />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest font-black text-blue-100">Próxima instrução</div>
                  <div className="text-sm font-black leading-tight mt-1">{instrucaoAtual?.text}</div>
                </div>
              </div>
            </div>
          )}

          {/* BOTÃO RECENTRALIZAR APARCE EXCLUSIVAMENTE QUANDO FOCO FOR DESATIVADO PELO TOQUE */}
          {!focoAtivo && (minhaPosicao || coordenadasPartida) && (
            <button onClick={recentralizarMapa} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-full shadow-2xl flex items-center gap-2 font-bold text-xs active:scale-95 pointer-events-auto border border-blue-500 animate-fade-in">
              <LocateFixed size={16} />
              Recentralizar
            </button>
          )}

          {!modoNavegacao && metricasLocais.distancia !== '0.0' && (
            <div className="w-full flex justify-center mb-1">
              <button onClick={dispararNavegacaoWaze} className="bg-blue-600 hover:bg-blue-700 text-white font-black text-xs px-6 py-3.5 rounded-full shadow-2xl flex items-center gap-2 uppercase tracking-wider active:scale-95 pointer-events-auto">
                <Compass size={15} />
                Iniciar Navegação
              </button>
            </div>
          )}

          {destinoSelecionado && (
            <div className="w-full bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-3xl p-4 shadow-2xl pointer-events-auto animate-slide-up flex flex-col gap-3">
              <div className="flex justify-between items-center border-b border-slate-100 dark:border-zinc-800 pb-2">
                <div className="flex items-center gap-2">
                  <GraduationCap size={18} className="text-rose-500" />
                  <span className="text-xs font-black uppercase tracking-tight text-slate-900 dark:text-white">Destino</span>
                </div>
                <button onClick={() => setDestinoSelecionado(null)} className="text-slate-400 bg-slate-100 dark:bg-zinc-800 p-1 rounded-full"><X size={14} /></button>
              </div>
              <div className="space-y-1">
                <h3 className="text-xs font-black text-slate-800 dark:text-zinc-100 leading-tight">{destinoSelecionado.nome}</h3>
                <div className="flex items-start gap-1 text-slate-600 dark:text-zinc-300">
                  <MapPin size={12} className="text-rose-500 mt-0.5 shrink-0" />
                  <span className="text-[11px] font-bold leading-tight">{destinoSelecionado.logradouro}</span>
                </div>
              </div>
              <button onClick={dispararNavegacaoWaze} className="w-full bg-rose-500 hover:bg-rose-600 text-white text-[11px] font-black py-3 rounded-2xl flex items-center justify-center gap-1 uppercase tracking-wider">
                <Navigation size={12} className="fill-white" />
                Ir para este local
              </button>
            </div>
          )}

          {alertaSelecionado && (
            <div className="w-full bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-3xl p-4 shadow-2xl pointer-events-auto animate-slide-up flex flex-col gap-3">
              <div className="flex justify-between items-center border-b border-slate-100 dark:border-zinc-800 pb-2">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></div>
                  <span className="text-xs font-black uppercase tracking-tight text-slate-900 dark:text-white">{alertaSelecionado.tipo}</span>
                </div>
                <button onClick={() => setAlertaSelecionado(null)} className="text-slate-400 bg-slate-100 dark:bg-zinc-800 p-1 rounded-full"><X size={14} /></button>
              </div>

              <div className="flex items-center justify-between gap-2">
                {listSlidesVisiveis.length > 1 && (
                  <button onClick={prevSlide} className="p-1 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-full shrink-0"><ChevronLeft size={16} /></button>
                )}
                <div className="space-y-1 flex-1 px-1">
                  <div className="flex items-start gap-1 text-slate-700 dark:text-zinc-200">
                    <MapPin size={12} className="text-blue-500 mt-0.5 shrink-0" />
                    <span className="text-[11px] font-bold leading-tight">{alertaSelecionado.logradouro || alertaSelecionado.rua || 'Buscando endereço...'}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[9px] text-slate-400 font-semibold">
                    <div>{formatarDataHora(alertaSelecionado.data_criacao || alertaSelecionado.createdAt)}</div>
                    <div className="truncate">Por: <span className="text-blue-500 font-bold">{alertaSelecionado.criado_por || 'Motorista'}</span></div>
                  </div>
                </div>
                {listSlidesVisiveis.length > 1 && (
                  <button onClick={nextSlide} className="p-1 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-full shrink-0"><ChevronRight size={16} /></button>
                )}
              </div>

              <div className="bg-slate-50 dark:bg-zinc-800/50 p-2 rounded-xl border border-slate-100 text-center">
                <p className="text-[10px] font-black text-slate-500 mb-1.5">Ainda está no local?</p>
                <div className="flex gap-2">
                  <button onClick={() => confirmarAlerta(alertaSelecionado.id)} className="flex-1 bg-blue-600 text-white text-[10px] font-black py-2 rounded-lg flex items-center justify-center gap-0.5">🟢 SIM ({alertaSelecionado.confirmacoes || 0})</button>
                  <button onClick={() => removerAlertaFalso(alertaSelecionado.id)} className="flex-1 bg-white dark:bg-zinc-700 text-slate-600 dark:text-zinc-200 text-[10px] font-black py-2 rounded-lg">🔴 NÃO</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PAINEL DE ALTERAÇÃO DE CAMADAS */}
      {mostrarPainelCamadas && (
        <div className="absolute top-20 right-16 z-[6000] bg-white/98 dark:bg-zinc-900/98 border border-slate-200 dark:border-zinc-800 rounded-3xl shadow-2xl p-4 w-[280px] pointer-events-auto animate-fade-in backdrop-blur-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Layers3 size={18} className="text-blue-500" />
              <span className="font-black text-sm text-slate-800 dark:text-white">Camadas do Mapa</span>
            </div>
            <button onClick={() => setMostrarPainelCamadas(false)} className="p-1 rounded-full bg-slate-100 dark:bg-zinc-800"><X size={14} /></button>
          </div>

          <div className="space-y-3">
            <button onClick={() => setMapStyleMode('streets')} className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition-all ${mapStyleMode === 'streets' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-slate-200 dark:border-zinc-800'}`}>
              <MapIcon size={18} />
              <div className="text-left">
                <div className="text-xs font-black">Navegação</div>
                <div className="text-[10px] text-slate-500">Melhor para rotas</div>
              </div>
            </button>

            <button onClick={() => setMapStyleMode('satellite')} className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition-all ${mapStyleMode === 'satellite' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-slate-200 dark:border-zinc-800'}`}>
              <Satellite size={18} />
              <div className="text-left">
                <div className="text-xs font-black">Satélite</div>
                <div className="text-[10px] text-slate-500">Visão aérea</div>
              </div>
            </button>

            <button onClick={() => setMapStyleMode('outdoors')} className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition-all ${mapStyleMode === 'outdoors' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-slate-200 dark:border-zinc-800'}`}>
              <Mountain size={18} />
              <div className="text-left">
                <div className="text-xs font-black">Relevo</div>
                <div className="text-[10px] text-slate-500">Trilhas e montanhas</div>
              </div>
            </button>

            <div className="border-t border-slate-100 dark:border-zinc-800 pt-3 mt-3 space-y-3">
              <SwitchRow icon={<School size={15} />} label="Prédios 3D" value={mostrarPredios3D} onChange={(v) => setMostrarPredios3D(v)} />
              <SwitchRow icon={<Mountain size={15} />} label="Relevo" value={mostrarRelevo} onChange={(v) => setMostrarRelevo(v)} />
              <SwitchRow icon={<GaugeIcon />} label="Radar Velocidade" value={mostrarRadarVelocidade} onChange={(v) => setMostrarRadarVelocidade(v)} />
            </div>
          </div>
        </div>
      )}

      {/* MENU LATERAL ESQUERDO */}
      <div className={`absolute inset-y-0 left-0 z-[5050] w-80 bg-white dark:bg-zinc-900 p-5 border-r border-slate-100 dark:border-zinc-800 shadow-2xl flex flex-col justify-between transition-transform duration-300 ease-out transform ${menuAberto ? 'translate-x-0' : '-translate-x-full'}`}>
        <div>
          <div className="flex justify-between items-center mb-6">
            <span className="text-base font-black text-slate-800 dark:text-white flex items-center gap-2">
              <Navigation size={18} className="text-blue-500" />
              Navegação
            </span>
            <button onClick={() => setMenuAberto(false)} className="text-slate-400 bg-slate-100 dark:bg-zinc-800 p-1.5 rounded-full"><X size={16} /></button>
          </div>

          <div className="bg-slate-50 dark:bg-zinc-800/40 p-3 rounded-2xl mb-4 flex items-center justify-between border border-slate-100 dark:border-zinc-800">
            <div className="flex items-center gap-2">
              {mostrarEventos ? <Eye size={16} className="text-blue-500" /> : <EyeOff size={16} className="text-slate-400" />}
              <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">Exibir alertas</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={mostrarEventos} onChange={() => setMostrarEventos(!mostrarEventos)} className="sr-only peer" />
              <div className="w-9 h-5 bg-slate-300 dark:bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="space-y-3.5">
            <div className="flex flex-col gap-1 bg-slate-50 dark:bg-zinc-800/40 p-3 rounded-2xl border border-slate-100 dark:border-zinc-800">
              <label className="text-[10px] font-bold text-blue-500">🟢 PARTIDA</label>
              <div className="flex items-center gap-1.5 mt-1">
                <input type="text" value={partidaTexto} onChange={(e) => setPartidaTexto(e.target.value)} className="w-full bg-transparent text-xs font-bold focus:outline-none text-slate-800 dark:text-zinc-200" placeholder="Cidade, rua..." />
                <button onClick={() => buscarEnderecoGeocode(partidaTexto, 'partida')} className="text-slate-400 hover:text-blue-500"><Search size={14} /></button>
              </div>
              <button onClick={aplicarGpsComoPartida} className="text-[9px] font-bold text-slate-400 hover:text-blue-500 text-left mt-2 flex items-center gap-1">
                <LocateFixed size={10} />
                Usar meu GPS atual
              </button>
            </div>

            <div className="flex flex-col gap-1 bg-slate-50 dark:bg-zinc-800/40 p-3 rounded-2xl border border-slate-100 dark:border-zinc-800">
              <label className="text-[10px] font-bold text-red-500">🔴 DESTINO</label>
              <div className="flex items-center gap-1.5 mt-1">
                <input type="text" value={destinoTexto} onChange={(e) => setDestinoTexto(e.target.value)} className="w-full bg-transparent text-xs font-bold focus:outline-none text-slate-800 dark:text-zinc-200" placeholder="Cidade, rua..." />
                <button onClick={() => buscarEnderecoGeocode(destinoTexto, 'destino')} className="text-slate-400 hover:text-red-500"><Search size={14} /></button>
              </div>
            </div>

            <button onClick={calcularNovaRotaCompleta} className="w-full bg-slate-800 hover:bg-slate-900 dark:bg-zinc-700 text-white font-extrabold text-xs py-3 rounded-2xl flex items-center justify-center gap-2 shadow-md active:scale-95 transition-transform mt-2">
              <Route size={14} />
              TRAÇAR ROTA
            </button>

            <button
              onClick={() => {
                setModoNavegacao(false);
                setInstrucaoAtual(null);
                setFocoAtivo(true);
                recentralizarMapa();
              }}
              className="w-full bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-200 font-extrabold text-xs py-3 rounded-2xl flex items-center justify-center gap-2 border border-slate-200 dark:border-zinc-700"
            >
              <RotateCcw size={14} />
              RESETAR MAPA
            </button>
          </div>
        </div>
        <div className="text-[10px] text-center text-slate-400 font-medium">Universo Mídia © 2026</div>
      </div>

      {/* SELEÇÃO DE TIPOS DE ALERTA NO PONTO CLICADO */}
      {pontoClique && (
        <div className="absolute bottom-0 left-0 right-0 z-[4006] bg-white dark:bg-zinc-900 px-4 pb-8 pt-5 rounded-t-[32px] shadow-[0_-10px_30px_rgba(0,0,0,0.15)] border-t border-slate-100 dark:border-zinc-800 animate-slide-up pointer-events-auto">
          <div className="w-12 h-1.5 bg-slate-200 dark:bg-zinc-700 rounded-full mx-auto mb-4" onClick={() => setPontoClique(null)}></div>
          <div className="mb-3 px-1 flex flex-col gap-0.5">
            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1">
              <MapPin size={12} />
              {nomeRuaAtual}
            </span>
            <div className="flex justify-between items-center">
              <span className="text-sm font-extrabold text-slate-800 dark:text-zinc-100 tracking-tight">O que está acontecendo aqui?</span>
              <button onClick={() => setPontoClique(null)} className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-3 py-1.5 rounded-full">Cancelar</button>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-2 overflow-x-auto pb-1">
            <AlertButton onClick={() => adicionarAlertaNoPonto('policia')} color="bg-blue-600" icon={<Siren size={18} />} label="Polícia" />
            <AlertButton onClick={() => adicionarAlertaNoPonto('transito')} color="bg-sky-500" icon={<Car size={18} />} label="Trânsito" />
            <AlertButton onClick={() => adicionarAlertaNoPonto('acidente')} color="bg-red-500" icon={<AlertTriangle size={18} />} label="Acidente" />
            <AlertButton onClick={() => adicionarAlertaNoPonto('obras')} color="bg-amber-500" icon={<Wrench size={18} />} label="Obras" />
            <AlertButton onClick={() => adicionarAlertaNoPonto('buraco')} color="bg-amber-900" icon={<AlertTriangle size={18} />} label="Buraco" />
          </div>
        </div>
      )}

      {/* CONTAINER DO MAPA CANVAS REAL */}
      <div ref={mapContainerRef} className="h-full w-full z-10" />

      {/* ESTILOS INTERNOS */}
      <style>{`
        .isolate-fixed-container {
          contain: layout;
          backface-visibility: hidden;
        }
        .animate-slide-up {
          animation: slideUp 0.25s cubic-bezier(0.1, 0.76, 0.55, 0.94) forwards;
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
        @keyframes slideUp {
          from { transform: translate3d(0, 100%, 0); }
          to { transform: translate3d(0, 0, 0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes pulseDriver {
          0% { transform: scale(0.9); opacity: 0.9; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        .mapboxgl-ctrl-bottom-right {
          bottom: 90px !important;
          right: 12px !important;
        }
        .mapboxgl-ctrl-group {
          border-radius: 18px !important;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.15);
          backdrop-filter: blur(10px);
        }
        .mapboxgl-ctrl button {
          width: 36px !important;
          height: 36px !important;
        }
      `}</style>
    </div>
  );
};

// COMPONENTES AUXILIARES
const AlertButton = ({ onClick, color, icon, label }) => {
  return (
    <button onClick={onClick} className="flex flex-col items-center justify-center p-2 bg-slate-50 dark:bg-zinc-800/50 rounded-xl border border-slate-100 dark:border-zinc-800 min-w-[65px]">
      <div className={`w-10 h-10 ${color} text-white rounded-full flex items-center justify-center mb-1 shadow-md`}>
        {icon}
      </div>
      <span className="text-[10px] font-bold text-slate-700 dark:text-zinc-300">{label}</span>
    </button>
  );
};

const SwitchRow = ({ icon, label, value, onChange }) => {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-slate-700 dark:text-zinc-300">
        {icon}
        <span className="text-xs font-bold">{label}</span>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
        <div className="w-9 h-5 bg-slate-300 dark:bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
  );
};

const GaugeIcon = () => {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-blue-500">
      <path d="M12 14l4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </svg>
  );
};

export default MapaEscola;